import { FEATURE_FLAGS } from "types-for/feature-flags";
import { FLAGSMITH_API_KEY, IDENTITY_FEATURE_STATES_API_URL } from "./feature-flag-toggler.constants";
import axios from "axios";

/**
 * Function to retrieve a sorted list of feature flags.
 * @returns {string[]} Array of feature flags sorted alphabetically.
 */
export const getSortedFeatureFlagsList = () => {
    return Object.values(FEATURE_FLAGS).sort((a: string, b: string) => {
        if (a < b) {
            return -1;
        }
        if (a > b) {
            return 1;
        }
        return 0;
    });
};

/**
 * Function to format a feature flag for display in a list.
 * @param {object} params - The parameters for formatting the feature flag.
 * @param {string} params.flagKey - The key of the feature flag.
 * @param {boolean} params.flagEnabled - The enabled state of the feature flag.
 * @returns {object} Formatted feature flag object with name, label, and enabled state.
 */
export const formatFeatureFlagForList = ({ flagKey, flagEnabled }: { flagKey: string; flagEnabled: boolean }) => {
    return {
        name: flagKey,
        label: flagKey
            .split("_")
            .map((word) => {
                return word[0].toUpperCase().concat(word.substr(1).toLowerCase());
            })
            .join(" "),
        enabled: flagEnabled,
    };
};

/**
 * Function to update the state of a feature flag for a specific user.
 * @param {object} params - The parameters for updating the flag state.
 * @param {string} params.flagKey - The key of the feature flag to update.
 * @param {boolean} params.state - The desired state of the feature flag (enabled or disabled).
 * @param {object} params.user - The user object containing user details.
 * @param {string} params.user.userId - The ID of the user.
 * @param {string} params.user.firstName - The first name of the user.
 * @param {string} params.user.surname - The surname of the user.
 * @returns {Promise} The axios promise for the PUT request to update the flag state.
 */
export const updateFlagState = ({
    flagKey,
    state,
    user: { userId, firstName, surname },
}: {
    flagKey: string;
    state: boolean;
    user: { userId: string; firstName: string; surname: string };
}) => {
    return axios.put(
        IDENTITY_FEATURE_STATES_API_URL,
        {
            enabled: state,
            feature: flagKey,
            feature_state_value: "",
            identifier: `${userId}_${firstName}_${surname}`,
        },
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${FLAGSMITH_API_KEY}`,
            },
        }
    );
};
